import { useEffect, useState } from "react";
import { ArrowUpRight, X } from "@phosphor-icons/react";
import { isIndia } from "@/lib/edition";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Reads visitor country (cached for the session) and shows a cross-brand switch
// banner. On the global site it invites India-located visitors to IndiaAlumni;
// on IndiaAlumni it invites outside-India visitors to the global site.
// When no geo-banner applies it renders `fallback` (the site's default top strip).
export default function GeoBanner({ fallback = null }) {
  // Optional preview/test override: ?geo=IN or ?geo=US forces a country code.
  const override = (() => {
    try { const g = new URLSearchParams(window.location.search).get("geo"); return g ? g.toUpperCase() : null; } catch { return null; }
  })();
  const [geo, setGeo] = useState(() => {
    if (override) return { country_code: override, country: override };
    try { const c = sessionStorage.getItem("ap_geo"); return c ? JSON.parse(c) : null; } catch { return null; }
  });
  const [ready, setReady] = useState(() => {
    if (override) return true;
    try { return !!sessionStorage.getItem("ap_geo"); } catch { return false; }
  });
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem("ap_geo_banner_dismissed") === "1"; } catch { return false; }
  });

  useEffect(() => {
    if (ready || override) return;
    let alive = true;
    fetch(`${API}/geo`)
      .then((r) => r.json())
      .then((d) => { if (!alive) return; setGeo(d); setReady(true); try { sessionStorage.setItem("ap_geo", JSON.stringify(d)); } catch { /* ignore */ } })
      .catch(() => { if (alive) setReady(true); });
    return () => { alive = false; };
  }, [ready]);

  const cc = geo && geo.country_code;
  const inIndia = cc === "IN";
  // Decide whether a geo banner applies for this edition + visitor location.
  const show = ready && !dismissed && cc && (isIndia ? !inIndia : inIndia);

  if (!show) return fallback;

  const target = isIndia
    ? { url: "https://alumnipulse.net", label: "AlumniPulse.net", text: "Looking for alumni outside India?" }
    : { url: "https://indiaalumni.com", label: "IndiaAlumni.com", text: "Looking for Indian alumni?" };

  const dismiss = (e) => { e.preventDefault(); e.stopPropagation(); setDismissed(true); try { sessionStorage.setItem("ap_geo_banner_dismissed", "1"); } catch { /* ignore */ } };

  return (
    <div data-testid="geo-switch-banner"
      className="relative flex items-center justify-center gap-2 bg-secondary px-10 py-2 text-center text-xs font-medium text-secondary-foreground sm:text-sm">
      <a href={target.url} className="group inline-flex items-center gap-2 hover:underline" data-testid="geo-switch-link">
        <span>{target.text} Visit <span className="font-semibold">{target.label}</span></span>
        <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5" />
      </a>
      <button onClick={dismiss} data-testid="geo-banner-dismiss"
        className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 opacity-70 transition-opacity hover:opacity-100" aria-label="Dismiss">
        <X size={14} weight="bold" />
      </button>
    </div>
  );
}
